import { flow, types as t } from 'mobx-state-tree';

import api from 'api';

import ApplicationForm from './ApplicationForm';
import companyStore from './CompanyStore';
import JobLanguage from './JobLanguage';
import StoreState from './StoreState';
import uiStore from './UIStore';

const searchable = (str) => str.toLowerCase().trim().replace(new RegExp(' ', 'g'), '');

export default t
  .model('Job', {
    id: t.identifierNumber,
    languages: t.array(JobLanguage),
    jobType: t.maybe(t.string),
    status: t.maybe(t.string),
    deadline: t.maybeNull(t.string),
    includeCv: t.maybe(t.boolean),
    requireCv: t.maybe(t.boolean),
    includeCoverLetter: t.maybe(t.boolean),
    requireCoverLetter: t.maybe(t.boolean),
    includePhone: t.maybe(t.boolean),
    requirePhone: t.maybe(t.boolean),
    includeSex: t.maybe(t.boolean),
    requireSex: t.maybe(t.boolean),
    includeSsn: t.maybe(t.boolean),
    requireSsn: t.maybe(t.boolean),
    department: t.maybeNull(t.number),
    location: t.maybeNull(t.number),
    form: t.maybe(ApplicationForm),

    state: t.optional(StoreState, 'initial'),
    formState: t.optional(StoreState, 'initial'),
    uuid: t.maybe(t.string),
    visibility: t.optional(t.string, 'public'),
  })
  .views((self) => ({
    get langData() {
      return self.languages.find((obj) => obj.language === uiStore.language);
    },
    get searchableTitle() {
      return searchable(self.title);
    },
    get title() {
      if (self.langData) {
        return self.langData.title;
      }
      return '';
    },
    get description() {
      if (self.langData) {
        return self.langData.description;
      }
      return '';
    },
    get shortDescription() {
      if (self.langData) {
        return self.langData.shortDescriptionHtml;
      }
      return '';
    },
    get locationDisplay() {
      // TODO: Remove once we remove language from langData
      const locations = companyStore.current.locations;
      let location = self.langData ? self.langData.location : '';
      if (locations && self.location) {
        const companyLocation = locations.find((location) => location.id === self.location);
        if (companyLocation) {
          const locationTranslation = companyLocation.translations.find(
            (translation) => translation.language === uiStore.language,
          );
          if (locationTranslation) {
            location = locationTranslation.title;
          }
        }
      }
      return location;
    },
    get sections() {
      if (self.langData) {
        return self.langData.form ? self.langData.form.sections : [];
      }
      return [];
    },
  }))
  .actions((self) => ({
    setUuid(uuid) {
      self.uuid = uuid;
    },
    load: flow(function* () {
      if (self.state !== 'initial') return;
      self.state = 'loading';
      let res;
      if (self.uuid) {
        res = yield api.jobs.loadDetailPreview(companyStore.current.name, self.id, self.uuid);
      } else {
        res = yield api.jobs.loadDetail(companyStore.current.name, self.id);
      }
      res.data.languages = res.data.languages.map((jl) => {
        if (!jl.shortDescriptionHtml) {
          jl.shortDescriptionHtml = jl.shortDescription;
        }
        return jl;
      });
      // API still returns old description values but we compute those in the getters above.
      const { id, title, description, shortDescription, location, department, ...rest } = res.data;
      Object.keys(rest).forEach((key) => (self[key] = rest[key]));
      self.state = 'loaded';
    }),
    loadForm: flow(function* () {
      if (self.formState !== 'initial') return;
      if (self.state === 'initial') {
        yield self.load();
      }
      self.formState = 'loading';
      let res;
      if (self.uuid) {
        res = yield api.jobs.loadFormPreview(companyStore.current.name, self.id, self.uuid);
      } else {
        res = yield api.jobs.loadForm(companyStore.current.name, self.id);
      }
      res.data.languages = res.data.languages.map((jl) => {
        let language = self.languages.find((l) => l.language === jl.language);
        language.form = jl.form;
        jl = language;
        return jl;
      });
      Object.keys(res.data).forEach((key) => (self[key] = res.data[key]));
      self.formState = 'loaded';
    }),
  }));
